import React, { useEffect, useState, useRef } from 'react'
import toast from "react-hot-toast";
import api from "../lib/axios";

// L5 audit: show the "your reservation is about to expire" prompt only in the
// final minute of the 5-minute hold (was firing at <= 240s, i.e. almost
// immediately after the timer started).
const EXTENSION_PROMPT_THRESHOLD_SECONDS = 60;

const CountdownTimer = (props) => {

    const { startTimerFlag, initialSeconds, values, updateReservationSecs } = props;
    const [time, setTime] = useState({});
    const [seconds, setSeconds] = useState(initialSeconds);
    const [timerStarted, setTimerStarted] = useState(false);
    const [showExtensionPopup, setShowExtensionPopup] = useState(false);
    const [extensionCancelled, setExtensionCancelled] = useState(false);
    const [extensionConfirmed, setExtensionConfirmed] = useState(false);
    // L4 audit: the interval handle used to live in a module-level `let timer`,
    // which meant a second mount of this component (or a Fast Refresh in dev)
    // would leak the previous interval. Scope it to the instance via useRef so
    // each mount owns its own handle and the cleanup effect can clear it.
    const timerRef = useRef(0);
    const secondsRef = useRef(initialSeconds);
    // Refs that mirror the current props/state so the unload handler can read
    // them without re-binding the listener whenever the parent re-renders
    // (which used to happen every step transition because `values` is a
    // different object identity each render).
    const valuesRef = useRef(values);
    const extensionConfirmedRef = useRef(extensionConfirmed);
    useEffect(() => { valuesRef.current = values; }, [values]);
    useEffect(() => { extensionConfirmedRef.current = extensionConfirmed; }, [extensionConfirmed]);

    const secondsToTime = (secs) => {
        let hours = Math.floor(secs / (60 * 60));

        let divisor_for_minutes = secs % (60 * 60);
        let minutes = Math.floor(divisor_for_minutes / 60);

        let divisor_for_seconds = divisor_for_minutes % 60;
        let seconds = Math.ceil(divisor_for_seconds);

        let obj = {
            "h": hours,
            "m": minutes,
            "s": seconds
        };
        return obj;
    };

    const countDown = () => {
        // Remove one second, set state so a re-render happens.
        const next = secondsRef.current - 1;
        secondsRef.current = next;
        setSeconds(next);
        setTime(secondsToTime(next));

        // Check if we're at zero.
        if (next <= 0) {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = 0;
            }
        } else if (next <= EXTENSION_PROMPT_THRESHOLD_SECONDS) {
            if (!showExtensionPopup && !extensionCancelled && !extensionConfirmed) {
                setShowExtensionPopup(true);
            }
        }
    }

    const startTimer = () => {
        if (timerRef.current === 0 && secondsRef.current > 0) {
            timerRef.current = setInterval(() => { countDown() }, 1000);
        }
    }

    const updateReservation = async () => {
        // Signed reservation JWT only — backend no longer accepts the
        // legacy raw u_b_id. If there's no JWT the extension can't proceed.
        const reservationCredential = localStorage.getItem("u_b_id_jwt");
        if (!reservationCredential) {
            toast.error("Reservation session expired — please reload the page.");
            return false;
        }

        let reserve_query =
            values.SelectedSlotId + "/" + encodeURIComponent(reservationCredential);
        // Public endpoint (auth:false). The reservationCredential above is
        // the per-session token the server uses to verify ownership.
        let reserve_result = await api.get("updateReserveSlot/" + reserve_query);

        let reservation_result = reserve_result.data.reservation_result;
        // console.log(reservation_result);
        if (!reservation_result.success || reservation_result.success == 0) {
            toast.error(reservation_result.message);
            return false;
        } else {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = 0;
            }
            updateReservationSecs(reservation_result.expiry_secs);
            secondsRef.current = reservation_result.expiry_secs;
            setShowExtensionPopup(false);
            setExtensionConfirmed(true);
            setTimerStarted(false);
        }
    };

    /* useEffect(() => {
        let timeLeftVar = secondsToTime(seconds);
        setTime(timeLeftVar);

        return () => {

        }
    }, []); */

    useEffect(() => {
        if (startTimerFlag && !timerStarted) {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = 0;
            }
            // Seed from the current prop, not from `seconds` state. `seconds`
            // was captured by useState(initialSeconds) on first render; if the
            // parent batched setInitialSeconds + setStartTimerFlag, both end
            // up correct, but reading the prop here removes the dependency on
            // useState init ordering entirely (audit HIGH 4).
            const seed = initialSeconds > 0 ? initialSeconds : seconds;
            secondsRef.current = seed;
            setSeconds(seed);
            setTime(secondsToTime(seed));
            setTimerStarted(true);
            startTimer();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timerStarted, startTimerFlag, initialSeconds])

    // Always tear the interval down when the component unmounts, regardless of
    // which path stopped the timer. This pairs with the useRef change above to
    // fully fix L4.
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = 0;
            }
        };
    }, []);

    // H3 audit: actively release the hold if the customer closes the tab or
    // navigates away mid-flow. We use navigator.sendBeacon so the request
    // reliably leaves the browser even during unload (axios would be cancelled).
    // The endpoint is idempotent and only clears the row that still belongs to
    // this token, so a late beacon from a closed tab can never clobber a fresh
    // hold by the same user in another tab.
    useEffect(() => {
        const handleUnload = () => {
            try {
                const v = valuesRef.current;
                const slotId = v && v.SelectedSlotId;
                // C4 audit: send the JWT if we have one, otherwise legacy id.
                const token = localStorage.getItem("u_b_id_jwt");
                if (!slotId || !token) return;
                // Don't release once the booking has completed — the server has
                // already cleared the hold and we'd be a no-op anyway.
                if (extensionConfirmedRef.current && secondsRef.current <= 0) return;

                const url =
                    (process.env.REACT_APP_API_URL || "") + "releaseReservation";
                const payload = JSON.stringify({
                    slot_id: slotId,
                    user_token: token,
                });

                if (navigator.sendBeacon) {
                    const blob = new Blob([payload], { type: "application/json" });
                    navigator.sendBeacon(url, blob);
                } else {
                    // Synchronous XHR fallback — only fires when sendBeacon is
                    // unavailable (very old browsers). releaseReservation is
                    // public on the backend (auth:false); the per-session
                    // reservation token in the body authenticates the request.
                    const xhr = new XMLHttpRequest();
                    xhr.open("POST", url, false);
                    xhr.setRequestHeader("Content-Type", "application/json");
                    xhr.send(payload);
                }
            } catch (_) {
                // Best-effort — never block unload over a failed release.
            }
        };
        window.addEventListener("beforeunload", handleUnload);
        window.addEventListener("pagehide", handleUnload);
        return () => {
            window.removeEventListener("beforeunload", handleUnload);
            window.removeEventListener("pagehide", handleUnload);
        };
        // Intentionally bind ONCE. Live values are read from refs above so we
        // don't churn add/remove listener pairs on every parent re-render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep secondsRef in sync if the parent re-seeds the countdown (e.g. after
    // an extension confirmed via updateReservation).
    useEffect(() => {
        secondsRef.current = initialSeconds;
    }, [initialSeconds]);

    /* useEffect(() => {
        console.log(seconds, time);

    }, [time, seconds]);
 */



    return (
        <>
            {time && <div className="text-center flex flex-col w-full items-center justify-center mb-2">
                <label className="text-xl">Time Left</label>
                <div className="text-2xl text-center flex w-full items-center justify-center">
                    <div className="w-24 mx-1 p-2 bg-yellow-500 text-white rounded-lg">
                        <div className="font-mono leading-none mb-2" x-text="minutes">{String(time.m).padStart(2, 0)}</div>
                        <div className="font-mono uppercase text-sm leading-none">Minutes</div>
                    </div>
                    <label className='text-yellow-500 font-bold'>:</label>
                    <div className="w-24 mx-1 p-2 bg-yellow-500 text-white rounded-lg">
                        <div className="font-mono leading-none mb-2" x-text="seconds">{String(time.s).padStart(2, 0)}</div>
                        <div className="font-mono uppercase text-sm leading-none">Seconds</div>
                    </div>
                </div>
            </div>}
            {(showExtensionPopup && !extensionCancelled && !extensionConfirmed && seconds > 0) && (
                <>
                    <div
                        className="overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none justify-center items-center bg-[#000000ad]"
                        id="modal-id"
                    >
                        <div className="relative w-auto my-6 mx-auto max-w-2xl">
                            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                                <div className="bg-white p-6  md:mx-auto">
                                    <svg width="24" height="24" viewBox="0 0 1000 1000" className="text-green-600 w-16 h-16 mx-auto my-6"><path fill="currentColor" d="M 500 0C 224 0 0 224 0 500C 0 776 224 1000 500 1000C 776 1000 1000 776 1000 500C 1000 224 776 0 500 0C 500 0 500 0 500 0M 500 25C 762 25 975 238 975 500C 975 762 762 975 500 975C 238 975 25 762 25 500C 25 238 238 25 500 25C 500 25 500 25 500 25M 526 150C 576 150 602 175 601 224C 600 300 600 350 575 525C 570 560 560 575 525 575C 525 575 475 575 475 575C 440 575 430 560 425 525C 400 355 400 300 400 226C 400 175 425 150 475 150M 500 650C 527 650 552 661 571 679C 589 698 600 723 600 750C 600 805 555 850 500 850C 445 850 400 805 400 750C 400 723 411 698 429 679C 448 661 473 650 500 650C 500 650 500 650 500 650" /></svg>
                                    <div className="text-center">
                                        <h3 className="md:text-2xl text-base text-gray-900 font-semibold text-center">
                                            Time Limit Will Expire in {String(time.s).padStart(2, 0)} seconds!
                                        </h3>
                                        <p className="text-gray-600 my-2">
                                            Your reservation will be released soon. Do you still want to continue the booking?
                                        </p>
                                        <div className="py-10 text-center">
                                            <a
                                                className="px-12 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-3"
                                                onClick={() => { updateReservation(); }}
                                            >
                                                Continue
                                            </a>
                                            <a
                                                className="ml-2 px-12 bg-red-600 hover:bg-red-500 text-white font-semibold py-3"
                                                onClick={() => {
                                                    setShowExtensionPopup(false);
                                                    setExtensionCancelled(true);
                                                }}
                                            >
                                                No Thanks
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        className="hidden opacity-25 fixed inset-0 z-40 bg-black"
                        id="modal-id-backdrop"
                    ></div>
                </>
            )}
            {(seconds == 0) && (
                <>
                    <div
                        className="overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none justify-center items-center bg-[#000000ad]"
                        id="modal-id"
                    >
                        <div className="relative w-auto my-6 mx-auto max-w-2xl">
                            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                                <div className="bg-white p-6  md:mx-auto">
                                    <svg width="24" height="24" viewBox="0 0 1000 1000" className="text-green-600 w-16 h-16 mx-auto my-6"><path fill="currentColor" d="M 500 0C 224 0 0 224 0 500C 0 776 224 1000 500 1000C 776 1000 1000 776 1000 500C 1000 224 776 0 500 0C 500 0 500 0 500 0M 500 25C 762 25 975 238 975 500C 975 762 762 975 500 975C 238 975 25 762 25 500C 25 238 238 25 500 25C 500 25 500 25 500 25M 526 150C 576 150 602 175 601 224C 600 300 600 350 575 525C 570 560 560 575 525 575C 525 575 475 575 475 575C 440 575 430 560 425 525C 400 355 400 300 400 226C 400 175 425 150 475 150M 500 650C 527 650 552 661 571 679C 589 698 600 723 600 750C 600 805 555 850 500 850C 445 850 400 805 400 750C 400 723 411 698 429 679C 448 661 473 650 500 650C 500 650 500 650 500 650" /></svg>
                                    <div className="text-center">
                                        <h3 className="md:text-2xl text-base text-gray-900 font-semibold text-center">
                                            Time Limit Reached!
                                        </h3>
                                        <p className="text-gray-600 my-2">
                                            Your reservation has been released. Please re-start your booking
                                        </p>
                                        <div className="py-10 text-center">
                                            <a
                                                href="/booking"
                                                className="px-12 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-3"
                                            >
                                                Back to Booking
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        className="hidden opacity-25 fixed inset-0 z-40 bg-black"
                        id="modal-id-backdrop"
                    ></div>
                </>
            )}
        </>
    )
}

export default CountdownTimer